import { Select, Spacing, Switch } from "@mindlercare/ui-web";
import CustomModal from "components/lib/customModal";
import { format, set } from "date-fns";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "components/typography";
import CONSTANTS from "../../../../core/constants";
import { useCalendarStartTime } from "components/booking/hooks/useCalendarStartTime";
import { useCalendarHideCancelledSlots } from "components/booking/hooks/useCalendarHideCancelledSlots";

type HourValue = {
  label: string;
  value: number;
};

const convertToLabel = (value: Date) => {
  return {
    label: format(value.getTime(), "HH:mm"),
    value: value.getTime(),
  };
};

const hours = [...Array(CONSTANTS.HOUR_PER_DAY).keys()];
const timeOptions = hours.map((x, i) => {
  return convertToLabel(
    set(new Date(), { hours: i, minutes: 0, seconds: 0, milliseconds: 0 }),
  );
});

export const CalendarSettingsDialog = ({
  open,
  close,
}: {
  open: boolean;
  close: () => void;
}) => {
  const [timeRangeMin, setCurrentStartTime] = useCalendarStartTime();
  const [hideCancelledSlots, setHideCancelledSlots] =
    useCalendarHideCancelledSlots();

  const { t } = useTranslation();

  const [newTimeRangeMin, setNewTimeRangeMin] = useState(timeRangeMin);
  const [newHideCanceledSlots, setNewHideCanceledSlots] =
    useState<boolean>(hideCancelledSlots);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      setNewHideCanceledSlots(!newHideCanceledSlots);
    },
    [newHideCanceledSlots],
  );

  const handleUpdate = ({ value }: HourValue) => {
    setNewTimeRangeMin(new Date(value));
  };

  const handleSave = () => {
    setHideCancelledSlots(newHideCanceledSlots);
    setCurrentStartTime(newTimeRangeMin);
    close();
  };

  const handleResetToSavedValues = useCallback(() => {
    close();
    setNewTimeRangeMin(timeRangeMin);
    setHideCancelledSlots(hideCancelledSlots);
  }, [close, timeRangeMin, setHideCancelledSlots, hideCancelledSlots]);

  const selectedTimeRangeMin = convertToLabel(new Date(newTimeRangeMin));

  return (
    <CustomModal.Wrapper
      size="large"
      onClose={() => handleResetToSavedValues()}
      titleText={t("CALENDAR_SETTINGS")}
      open={open}
    >
      <CustomModal.Paragraph>
        {t("CALENDAR_SETTINGS_DIALOG_TEXT")}
      </CustomModal.Paragraph>
      <Select
        id="calendar-start-hour"
        label={t("START_HOUR")}
        value={selectedTimeRangeMin.value.toString()}
      >
        <Select.Trigger>{selectedTimeRangeMin.label}</Select.Trigger>
        <Select.Options>
          {timeOptions.map((option) => (
            <Select.Option
              key={option.value}
              onClick={() => handleUpdate(option)}
              value={option.value.toString()}
            >
              {option.label}
            </Select.Option>
          ))}
        </Select.Options>
      </Select>
      <Spacing t={16} />
      <CustomModal.SwitchDropdownDiv>
        <Typography variant="body-small" insensitive>
          {t("CALENDAR_SETTINGS_HIDE_CANCELLED_MEETINGS")}
        </Typography>
        <Switch isActive={newHideCanceledSlots} onClick={handleClick} />
      </CustomModal.SwitchDropdownDiv>
      <CustomModal.ButtonRow paddingTop={24}>
        <CustomModal.Button
          variant="secondary"
          onClick={() => handleResetToSavedValues()}
          text={t("CANCEL")}
        />
        <CustomModal.Button
          variant="primary"
          onClick={() => handleSave()}
          text={t("SAVE")}
        />
      </CustomModal.ButtonRow>
    </CustomModal.Wrapper>
  );
};
